import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, clearError } from "../../redux/authSlice";
import { getUserTestResults, getAllTests } from "../../redux/testSlice";
import { SURVEY_MAPPED_TYPE } from "../../constants/testTypes";
import {
  forgotPassword,
  clearForgotPasswordMessage,
} from "../../redux/userSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ForgotPasswordModal from "./ForgotPasswordModal";

const SignInForm = () => {
  const extractData = (payload) => payload?.data ?? payload;
  const activeTab = "email";
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+90");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, loading, error, isAccountVerified } = useSelector(
    (state) => state.auth,
  );
  const {
    forgotPasswordMessage,
    loading: userLoading,
  } = useSelector((state) => state.users);
  const { t } = useTranslation();
  const [hasNavigated, setHasNavigated] = useState(false);
  const setEnglishValidity = (e) => {
    e.target.setCustomValidity("Please fill out this field.");
  };
  const clearCustomValidity = (e) => {
    e.target.setCustomValidity("");
  };

  useEffect(() => {
    const checkTestResultsAndNavigate = async () => {
      if (token && user && !hasNavigated) {
        setHasNavigated(true);
        const userType = localStorage.getItem("userType");

        switch (userType) {
          case "admin":
            navigate("/adminHome");
            break;
          case "therapist":
            navigate("/therapistHomePage");
            break;
          case "user":
          default:
            try {
              // 1. Kullanıcının test sonuçlarını getir (404 = yeni kullanıcı, boş kabul et)
              let userResults = [];
              try {
                const resultsAction = await dispatch(
                  getUserTestResults(user._id),
                ).unwrap();
                userResults = extractData(resultsAction) || [];
              } catch {
                userResults = [];
              }

              // 2. Tüm aktif testleri getir
              const allTestsAction = await dispatch(getAllTests()).unwrap();
              const allTests = extractData(allTestsAction) || [];

              // 3. Aktif onboarding testlerinden kullanıcının henüz doldurmadığı var mı kontrol et
              const pendingRegistrationSurveys = allTests.filter(
                (test) =>
                  test.isActive &&
                  test.type === SURVEY_MAPPED_TYPE &&
                  !userResults.some(
                    (result) =>
                      result.testId?._id === test._id ||
                      result.testId === test._id,
                  ),
              );

              if (pendingRegistrationSurveys.length > 0) {
                navigate("/test-solve", { state: { isOnboarding: true } });
              } else {
                navigate("/userHomePage");
              }
            } catch (err) {
              console.error("Error in onboarding flow check:", err);
              navigate("/userHomePage");
            }
            break;
        }
      }
    };

    checkTestResultsAndNavigate();
  }, [token, user, navigate, hasNavigated, dispatch]);

  useEffect(() => {
    if (isAccountVerified === false && email) {
      navigate("/signIn", {
        state: {
          email: email,
          message:
            "Your email address has not been verified yet. Please check your email or request a new verification link.",
          status: "pending",
        },
      });
    }
  }, [isAccountVerified, email, navigate]);

  useEffect(() => {
    if (forgotPasswordMessage) {
      toast.success(forgotPasswordMessage);
      setShowForgotModal(false);
      dispatch(clearForgotPasswordMessage());
    }
  }, [forgotPasswordMessage, dispatch]);


  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearForgotPasswordMessage());
    };
  }, [dispatch]);

  // Load remembered email
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedUserEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    let loginPayload = { password };
    if (activeTab === "email") {
      loginPayload.email = email;
    } else {
      loginPayload.phone = phone;
      loginPayload.countryCode = countryCode;
    }

    try {
      await dispatch(loginUser(loginPayload)).unwrap();

      // If remember option is checked, save to local storage
      if (rememberMe) {
        localStorage.setItem("rememberedUserEmail", email);
      } else {
        localStorage.removeItem("rememberedUserEmail");
      }

      toast.success("Sign in successful!");
    } catch (err) {
      console.error("Sign-in error:", err);
      toast.error(err || "An error occurred while signing in");
    }
  };

  const handleForgotPassword = async (email) => {
    try {
      await dispatch(forgotPassword({ email })).unwrap();
    } catch (err) {
      toast.error(err || "User not found.");
      throw err;
    }
  };

  return (
    <div className="mt-[32px]">
      {/* Email/Phone Tabs */}
      {/* <div className="flex justify-between items-center bg-[#FFFFFF] rounded-[30px] h-[37px] px-2 py-1 mb-6">
        <button
          type="button"
          onClick={() => setActiveTab("email")}
          className={`w-1/2 h-[26px] text-center font-poppins text-[20px] rounded-full ${
            activeTab === "email" ? "bg-button-bg text-white" : "text-text"
          }`}
        >
          E-mail
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("phone")}
          className={`w-1/2 h-[26px] text-center font-poppins text-[20px] rounded-full ${
            activeTab === "phone" ? "bg-button-bg text-white" : "text-text"
          }`}
        >
          {t("phone")}
        </button>
      </div> */}

      {/* Instruction Text */}
      <p className="text-text font-poppins text-[20px] ml-4 mb-4">
        {t("signInTitle")}
      </p>

      {/* Login Form */}
      <form onSubmit={handleLogin} lang="en">
        <div className="overflow-hidden">
          <AnimatePresence mode="wait">
            {activeTab === "email" && (
              <motion.div
                key="email"
                initial={{ opacity: 0, x: "-10%" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "10%" }}
                transition={{ duration: 0.3 }}
              >
                {/* Email Input */}
                <input
                  type="email"
                  placeholder="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  onInvalid={setEnglishValidity}
                  onInput={clearCustomValidity}
                  className="w-full px-4 py-2 border-2 border-cards-border focus:ring-2 focus:ring-[#1B5E20] rounded-tr-[30px] rounded-tl-[30px]"
                />

                {/* Password Input */}
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    onInvalid={setEnglishValidity}
                    onInput={clearCustomValidity}
                    className="w-full px-4 py-2 pr-12 border-2 border-cards-border rounded-bl-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
                  />
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-8 top-1/2 -translate-y-1/2"
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </div>

                {/* Checkbox Area */}
                <div className="flex items-center border-cards-border mt-4 ml-4">
                  <input
                    type="checkbox"
                    id="remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-6 h-6 text-text border-[3px] border-cards-border rounded-full"
                  />
                  <label
                    htmlFor="remember"
                    className="ml-2 text-[20px] font-poppins text-text"
                  >
                    {t("remember")}
                  </label>
                </div>
              </motion.div>
            )}

            {activeTab === "phone" && (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: "10%" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "-10%" }}
                transition={{ duration: 0.3 }}
              >
                {/* Phone Input with Country Code */}
                <div className="flex items-center border-2 border-cards-border rounded-tl-[30px] rounded-tr-[30px]">
                  {/* Country Code Dropdown */}
                  <select
                    className="px-4 py-2 text-[16px] text-text border-r-2 border-cards-border font-poppins outline-none focus:ring-2 focus:ring-[#1B5E20] rounded-tl-[30px]"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+90">+90</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+49">+49</option>
                  </select>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    placeholder={t("phone")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    pattern="[0-9]{10}"
                    onInvalid={setEnglishValidity}
                    onInput={clearCustomValidity}
                    className="w-full px-4 py-2 bg-white text-[16px] rounded-tr-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
                  />
                </div>

                {/* Password Input */}
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-2 pr-12 border-2 border-cards-border rounded-bl-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
                  />
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-8 top-1/2 -translate-y-1/2"
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </div>

                {/* Checkbox Area */}
                <div className="flex items-center mt-4 ml-4">
                  <input
                    type="checkbox"
                    id="remember-phone"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-6 h-6 text-text border-[3px] border-cards-border rounded-full"
                  />
                  <label
                    htmlFor="remember-phone"
                    className="ml-2 text-[20px] font-poppins text-text"
                  >
                    {t("remember")}
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-red-500 text-center mt-4 font-poppins">{error}</p>
        )}

        {/* Forgot Password Link */}
        <div className="text-right mt-3">
          <button
            type="button"
            onClick={() => setShowForgotModal(true)}
            className="text-text hover:text-text-dark text-sm font-poppins"
          >
            {t("forgotPassword")}
          </button>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={`w-full h-[61px] ${
            loading ? "bg-gray-400" : "bg-button-bg hover:bg-button-bg-hover"
          } text-white py-2 rounded-full text-[20px] font-poppins mt-6`}
          disabled={loading}
        >
          {loading ? "Signing in..." : t("signIn")}
        </button>
      </form>

      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        onSend={handleForgotPassword}
        loading={userLoading}
      />
    </div>
  );
};

export default SignInForm;
